import React, { useRef, useState } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../../shared/api/firebase/config';
import { verifyToken, verifyPin } from '../../services/MfaService';

const OFFICE_WHATSAPP = '5541998481711';

function buildWhatsAppLink(message) {
  return `https://wa.me/${OFFICE_WHATSAPP}?text=${encodeURIComponent(message)}`;
}

function PinInfoPopover({ onClose }) {
  return (
    <div className="absolute left-0 top-6 z-50 w-72 rounded-xl border border-[#C5A059]/40 bg-white shadow-xl p-4">
      <button
        type="button"
        onClick={onClose}
        className="absolute top-2 right-3 text-gray-400 hover:text-gray-700 text-base leading-none"
        aria-label="Fechar"
      >
        ×
      </button>
      <p className="text-[11px] font-extrabold text-[#20243B] uppercase tracking-widest mb-2">O que é o PIN?</p>
      <p className="text-xs text-gray-700 leading-relaxed mb-3">
        O PIN é uma <strong>senha de 4 números</strong> que <strong>você mesmo criou</strong> na primeira vez que acessou o Portal RDA.
        É diferente da sua senha de e-mail — é exclusivo deste portal.
      </p>
      <p className="text-[11px] font-extrabold text-[#20243B] uppercase tracking-widest mb-1.5">Não lembra o PIN?</p>
      <p className="text-xs text-gray-600 leading-relaxed mb-3">
        Clique no botão do WhatsApp abaixo para falar com o escritório. Em poucos minutos seu acesso será redefinido.
      </p>
      <a
        href={buildWhatsAppLink('Olá! Esqueci meu PIN de acesso ao Portal RDA. Preciso de ajuda para redefinir o acesso.')}
        target="_blank"
        rel="noreferrer"
        className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-white text-xs font-extrabold uppercase tracking-widest"
        style={{ background: '#25D366' }}
      >
        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        Falar com o escritório
      </a>
    </div>
  );
}

function PinInput({ value, onChange, status, disabled }) {
  const refsArr = useRef([]);

  const focus = (i) => refsArr.current[i]?.focus();

  const PIN_LEN = 4;

  const handleKey = (i, e) => {
    const digit = e.target.value.replace(/\D/g, '').slice(-1);
    const next = value.split('');
    next[i] = digit;
    const joined = next.join('').slice(0, PIN_LEN);
    onChange(joined);
    if (digit && i < PIN_LEN - 1) setTimeout(() => focus(i + 1), 0);
  };

  const handleBackspace = (i, e) => {
    if (e.key === 'Backspace' && !value[i] && i > 0) focus(i - 1);
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, PIN_LEN);
    if (pasted) { onChange(pasted); setTimeout(() => focus(Math.min(pasted.length - 1, PIN_LEN - 1)), 0); }
    e.preventDefault();
  };

  return (
    <div className="flex gap-3 justify-center" onPaste={handlePaste}>
      {Array.from({ length: PIN_LEN }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { refsArr.current[i] = el; }}
          type="password"
          inputMode="numeric"
          maxLength={1}
          value={value[i] ? '•' : ''}
          onChange={(e) => handleKey(i, e)}
          onKeyDown={(e) => handleBackspace(i, e)}
          autoFocus={i === 0}
          disabled={disabled}
          className={`w-14 h-16 text-center text-2xl font-bold rounded-xl border-2 outline-none transition-all ${
            status === 'error'
              ? 'border-red-400 bg-red-50 text-red-700'
              : status === 'success'
              ? 'border-green-400 bg-green-50 text-green-700'
              : value[i]
              ? 'border-[#C5A059] bg-amber-50 text-[#20243B]'
              : 'border-gray-200 bg-gray-50 text-[#20243B] focus:border-[#C5A059] focus:bg-white'
          } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        />
      ))}
    </div>
  );
}

export default function MfaGate({ secret, pinHash, onVerified }) {
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('idle');
  const [attempts, setAttempts] = useState(0);
  const [showInfo, setShowInfo] = useState(false);
  const isPinMode = Boolean(pinHash);
  const PIN_LEN = 4;

  const verifyCode = async (token = code) => {
    const expectedLen = PIN_LEN;
    if (token.length !== expectedLen || status === 'checking' || status === 'success') return;
    setStatus('checking');
    const valid = isPinMode
      ? await verifyPin(token, pinHash)
      : await verifyToken(token, secret);
    if (valid) {
      setStatus('success');
      setTimeout(() => onVerified?.(), 400);
    } else {
      const next = attempts + 1;
      setAttempts(next);
      setStatus('error');
      setCode('');
    }
  };

  const handleChange = (v) => {
    setCode(v);
    setStatus('idle');
    if (v.length === PIN_LEN) verifyCode(v);
  };

  const handleSignOut = async () => {
    try { await signOut(auth); } catch {}
    try { sessionStorage.clear(); } catch {}
    window.location.href = '/';
  };

  const isChecking = status === 'checking' || status === 'success';
  const ready = code.length === PIN_LEN && !isChecking;

  return (
    <div className="w-full max-w-xl mx-auto">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">

        {/* ── HEADER ── */}
        <div className="bg-[#20243B] px-6 py-5 relative overflow-hidden">
          <div className="absolute inset-0 opacity-5"
            style={{ backgroundImage: 'repeating-linear-gradient(45deg,#C5A059 0,#C5A059 1px,transparent 0,transparent 50%)', backgroundSize: '12px 12px' }}
          />
          <div className="relative">
            <h1 className="text-sm font-extrabold text-white uppercase tracking-widest leading-tight">
              Verificação de Segurança
            </h1>
            <p className="text-xs font-semibold text-white/80 mt-1.5 leading-relaxed max-w-sm">
              {isPinMode ? (
                <><strong className="text-white">Acesso protegido.</strong>{' '}Digite o PIN de 4 dígitos que você criou no primeiro acesso ao portal.</>
              ) : (
                <><strong className="text-white">Acesso protegido.</strong>{' '}Abra o seu app autenticador e confirme o código de 6 dígitos para continuar.</>
              )}
            </p>
          </div>
        </div>

        <div className="divide-y divide-gray-100">

          {/* ══ CONFIRMAÇÃO PIN ══ */}
          <div className="px-6 py-4">
            <div className="flex items-center gap-2 mb-3 relative">
              <p className="text-[10px] font-extrabold text-[#20243B] uppercase tracking-widest">
                {isPinMode ? 'Digite seu PIN de 4 dígitos:' : 'Digite o código de 6 dígitos gerado no app:'}
              </p>
              {isPinMode && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowInfo(v => !v)}
                    className="w-4 h-4 rounded-full border border-[#C5A059] text-[#C5A059] flex items-center justify-center text-[9px] font-extrabold leading-none hover:bg-[#C5A059] hover:text-white transition-colors"
                    aria-label="O que é o PIN?"
                  >
                    i
                  </button>
                  {showInfo && <PinInfoPopover onClose={() => setShowInfo(false)} />}
                </div>
              )}
            </div>

            <PinInput
              value={code}
              onChange={handleChange}
              status={status}
              disabled={isChecking}
            />

            {status === 'error' && (
              <p className="mt-2.5 text-xs text-red-600 font-semibold text-center flex items-center justify-center gap-1.5">
                <i className="fas fa-times-circle" />
                Código incorreto.{attempts >= 3 ? ' Verifique o horário do celular.' : ' Tente novamente.'}
              </p>
            )}
            {status === 'success' && (
              <p className="mt-2.5 text-xs text-green-700 font-bold text-center flex items-center justify-center gap-1.5">
                <i className="fas fa-check-circle" />
                Acesso liberado!
              </p>
            )}
            {status === 'checking' && (
              <p className="mt-2.5 text-xs text-gray-500 text-center flex items-center justify-center gap-1.5">
                <span className="w-3 h-3 rounded-full border-2 border-gray-300 border-t-[#C5A059] animate-spin" />
                Verificando...
              </p>
            )}

            <button
              type="button"
              onClick={() => verifyCode()}
              disabled={!ready}
              className="mt-4 w-full py-3 rounded-xl font-extrabold uppercase tracking-widest text-xs transition-all flex items-center justify-center gap-2"
              style={{
                background: ready ? '#C5A059' : '#e5e7eb',
                color: ready ? 'white' : '#9ca3af',
                cursor: ready ? 'pointer' : 'not-allowed',
              }}
            >
              {status === 'checking' ? (
                <>
                  <span className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  Verificando...
                </>
              ) : (
                <>
                  <i className="fas fa-lock" />
                  Confirmar Acesso
                </>
              )}
            </button>

            {attempts >= 3 && (
              <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
                <p className="text-[10px] text-amber-800 leading-relaxed text-center">
                  {isPinMode
                    ? <><strong>Bloqueado?</strong> Entre em contato com o escritório para redefinir o PIN.</>  
                    : <><strong>Dica:</strong> Certifique-se de que o horário do seu celular está sincronizado automaticamente.</>}
                </p>
              </div>
            )}
          </div>

          {/* ══ FOOTER ══ */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 space-y-3">
            <p className="text-[11px] font-semibold text-gray-600 text-center leading-relaxed">
              {isPinMode
              ? <><strong className="text-gray-700">Esqueceu o PIN?</strong><br />Entre em contato com o escritório para redefinição de acesso.</>
              : <><strong className="text-gray-700">Perdeu o acesso ao seu celular?</strong><br />Entre em contato com o suporte do escritório para recuperação do acesso.</>
            }
            </p>

            {isPinMode && (
              <a
                href={buildWhatsAppLink('Olá! Esqueci meu PIN de acesso ao Portal RDA e preciso de ajuda para redefinir o acesso.')}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-white text-xs font-extrabold uppercase tracking-widest transition-opacity hover:opacity-90"
                style={{ background: '#25D366' }}
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Falar com o escritório pelo WhatsApp
              </a>
            )}

            <div className="flex justify-center">
              <button
                type="button"
                onClick={handleSignOut}
                className="text-[10px] text-gray-400 hover:text-red-500 font-medium uppercase tracking-wide transition-colors flex items-center gap-1.5"
              >
                <i className="fas fa-sign-out-alt text-[10px]" />
                Encerrar sessão
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
